import { useEffect, useRef, useState } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Divider, InputRef, Typography } from 'antd';

import { CategoryTreeItem } from '~apis/categories';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { TreeData, TreeNodeId } from '~components/ui/HorizontalTreeTable/utils';
import {
  useCategoriesTree,
  useMoveParentCategory,
  useReorderCompanySpecificCategories,
  useToggleActiveCategory,
} from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CashFlowType } from '~models/cashFlow';
import { mixpanel } from '~pages/_app';

import CategoryDescriptionTextArea from './CategoryDescriptionTextArea';
import CategoryNameInput from './CategoryNameInput';
import CategoryTreeSelect from './CategoryTreeSelect';
import InfoItem from './InfoItem';
import ActiveToggleSection from './common/component/ActiveToggleSection';
import ReorderSection from './common/component/OrderChangeSection';

const Info: React.FC<
  {
    depth: number;
    namePath: string;
    activeKey: string;
    activeChildren: TreeData<CategoryTreeItem>[];
    parentKey: TreeNodeId | null;
  } & CategoryTreeItem
> = ({ depth, activeChildren, activeKey, parentKey, ...props }) => {
  const { companyId } = useDemoAwareCompanyId();
  const inputRef = useRef<InputRef>(null);
  const [orderPosition, setOrderPosition] = useState<string>('');
  const [isActive, setIsActive] = useState(props.isActive);

  const { data } = useCategoriesTree({ companyId });
  const categories = data?.Data?.data?.tree?.children;
  const flattenedTree = flattenTree(categories ?? []).filter(
    (item) =>
      !!item.value?.name &&
      props.cashFlowType === item.value?.cashFlowType &&
      !item.value?.contraCategoryId,
  );

  const { mutateAsync: toggleCategory } = useToggleActiveCategory();
  const { mutateAsync: moveParentCategory, isLoading: isMoveParentLoading } =
    useMoveParentCategory();
  const { mutateAsync: reorder, isLoading } = useReorderCompanySpecificCategories();

  const parentCategory = flattenedTree.find((item) => item.key === parentKey);
  const category = props.categoryLabelKey?.split('_');
  const categoryId = Number(category?.[0]);
  const id = Number(category?.[1]);
  const categoryIds =
    parentCategory?.children.filter((item) => item.status !== 'new').map((item) => item.value.id) ??
    [];

  const childrenTarget = parentCategory?.children.filter((item) => item.status !== 'new') || [];
  const childrenIndex = childrenTarget.findIndex((item) => item.key === activeKey) ?? 0;

  const orderUpDisable = childrenIndex === 0;
  const orderDownDisable = childrenIndex === (childrenTarget.length ?? 0) - 1;

  const parentTarget = flattenedTree.find((item) => item.key === parentKey);
  const childrenTree = flattenTree(activeChildren ?? []).filter((item) => item.children.length > 0);
  const filteredChildrenTree = [parentTarget, ...childrenTree].map((item) => item?.key);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsActive(props.isActive);
  }, [props.description, props.isActive]);

  const onMoveParentCategory = async (newId: number, newType: string) => {
    const response = await moveParentCategory({
      companyId,
      categoryId,
      id,
      newParentKey: {
        id: newId,
        type: newType,
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('상위 계정이 변경되었습니다.');
      mixpanel.track(MIXPANEL_EVENT.CATEGORY.CHANGE_PARENT_CATEGORY);
      mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
        type: 'CHANGE_PARENT_CATEGORY',
      });
    } else {
      Toast.error('상위 계정 변경에 실패했습니다.');
    }
  };

  const onReorder = async (position: number) => {
    // categoryIds에서 기존 index를 찾아서 position으로 변경
    function moveTargetToIndex(arr: number[], newIndex: number) {
      const index = arr.indexOf(props.id); // target의 현재 인덱스 찾기
      if (index === -1 || newIndex < 0 || newIndex >= arr.length) {
        return arr; // target이 없거나 newIndex가 범위를 벗어나면 원본 배열 반환
      }

      // target 값을 제거하고 새로운 위치에 삽입
      arr.splice(index, 1); // 기존 위치에서 제거
      arr.splice(newIndex, 0, props.id); // 새로운 위치에 삽입

      return arr;
    }

    // 예제 사용

    const result = moveTargetToIndex(categoryIds, position);

    const response = await reorder({
      companyId,
      categoryId,
      request: {
        ids: result,
        parentCategoryTreeKey: parentCategory?.key ?? '',
      },
    });

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('계정 순서가 변경되었습니다.');
    } else {
      Toast.error('계정 순서 변경에 실패했습니다.');
    }
  };

  switch (depth) {
    case 1:
      return (
        <Flex gap={12} direction="column">
          <InfoItem
            title="구분"
            description={props.cashFlowType === CashFlowType.IN ? '입금' : '출금'}
          />
          <InfoItem title="계정 이름" description={props.name} />
        </Flex>
      );
    case 2:
      return (
        <Flex gap={12} direction="column">
          <InfoItem
            title="구분"
            description={props.cashFlowType === CashFlowType.IN ? '입금' : '출금'}
          />
          <InfoItem title="계정 이름" description={props.name} />
          <InfoItem title="계정 설명" description={props.description} />
          <InfoItem title="상위 계정" description={parentCategory?.name} />
          <Divider />
        </Flex>
      );
    default:
      return (
        <Flex gap={12} direction="column">
          <InfoItem
            title="구분"
            description={props.cashFlowType === CashFlowType.IN ? '입금' : '출금'}
          />
          <InfoItem
            title="계정 이름"
            titleStyle={{
              display: 'inline-block',
              paddingTop: 6,
            }}
            description={
              <CategoryNameInput
                inputRef={inputRef}
                id={id}
                parentId={
                  (parentKey as string)?.split('_')[1]
                    ? Number((parentKey as string)?.split('_')[1])
                    : null
                }
                categoryId={categoryId}
                name={props.name}
                description={props.description ?? undefined}
              />
            }
          />
          <InfoItem
            title="계정 설명 (선택)"
            titleStyle={{
              display: 'inline-block',
              paddingTop: 6,
            }}
            description={
              <CategoryDescriptionTextArea
                id={id}
                parentId={
                  (parentKey as string)?.split('_')[1]
                    ? Number((parentKey as string)?.split('_')[1])
                    : null
                }
                categoryId={categoryId}
                name={props.name}
                description={props.description ?? undefined}
              />
            }
          />
          <InfoItem
            title="상위 계정"
            description={
              <Flex gap={8} align="center">
                <Typography.Text
                  style={{
                    ...typographyStyle['14_400'],
                    color: Colors.gray900,
                    width: 'max-content',
                  }}
                >
                  {parentCategory?.value?.name}
                </Typography.Text>
                <CategoryTreeSelect
                  disableScroll
                  treeRef={wrapperRef}
                  disableActionOnInactiveItem={false}
                  treeList={flattenedTree.map((item) => {
                    return {
                      ...item,
                      isDisabled:
                        item.key === activeKey ||
                        (filteredChildrenTree.includes(item.key) && depth - 1 !== item.depth),
                    };
                  })}
                  defaultActiveKey={parentCategory?.key ? [parentCategory?.key] : []}
                  onApply={(item) => {
                    if (!item?.value) return;
                    onMoveParentCategory(item?.value?.id, item?.value?.type);
                  }}
                  applyLoading={isMoveParentLoading}
                />
              </Flex>
            }
          />
          <ActiveToggleSection
            isActive={isActive}
            onToggle={async (checked) => {
              const response = await toggleCategory({
                companyId,
                categoryId,
                id,
                active: checked,
              });

              if (response.Status === ResponseStatus.SUCCESS) {
                Toast.success('계정 활성화 여부가 변경되었습니다.');
                setIsActive(checked);
              } else {
                Toast.error('계정 활성화 여부 변경에 실패했습니다.');
              }
            }}
          />
          <ReorderSection
            orderDownDisable={orderDownDisable}
            orderUpDisable={orderUpDisable}
            orderPosition={orderPosition}
            isLoading={isLoading}
            onArrowUpClick={() => {
              const position = categoryIds.findIndex((categoryId) => categoryId === id);
              setOrderPosition('up');
              onReorder(position - 1);
            }}
            onArrowDownClick={() => {
              const position = categoryIds.findIndex((categoryId) => categoryId === id);
              setOrderPosition('down');
              onReorder(position + 1);
            }}
          />

          <Divider />
        </Flex>
      );
  }
};

export default Info;
